// src/components/loyalty/CreateProgramModal.js
import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const CreateProgramModal = ({ isOpen, onClose, onCreate, onEdit, programToEdit }) => {
    const [programName, setProgramName] = useState(programToEdit?.name || '');
    const [programDescription, setProgramDescription] = useState(programToEdit?.description || '');
    const [programType, setProgramType] = useState(programToEdit?.type || 'Point-based');
    const [pointConfig, setPointConfig] = useState({
        earnRate: programToEdit?.earnRate || '1 point per $1 spent',
        redemptionRate: programToEdit?.redemptionRate || '100 points = $5 discount',
    });
    const [tieredConfig, setTieredConfig] = useState({
        tiers: programToEdit?.tiers || [{ name: 'Bronze', criteria: '', benefits: '' }],
    });
    const [spendConfig, setSpendConfig] = useState({
        thresholds: programToEdit?.thresholds || [{ amount: '', discount: '' }],
    });

    if (!isOpen) return null;

    const handleAddTier = () => {
        setTieredConfig({
            ...tieredConfig,
            tiers: [...tieredConfig.tiers, { name: '', criteria: '', benefits: '' }],
        });
    };

    const handleUpdateTier = (index, field, value) => {
        const updatedTiers = [...tieredConfig.tiers];
        updatedTiers[index][field] = value;
        setTieredConfig({ ...tieredConfig, tiers: updatedTiers });
    };

    const handleRemoveTier = (index) => {
        const updatedTiers = tieredConfig.tiers.filter((_, i) => i !== index);
        setTieredConfig({ ...tieredConfig, tiers: updatedTiers });
    };

    const handleAddThreshold = () => {
        setSpendConfig({
            ...spendConfig,
            thresholds: [...spendConfig.thresholds, { amount: '', discount: '' }],
        });
    };

    const handleUpdateThreshold = (index, field, value) => {
        const updatedThresholds = [...spendConfig.thresholds];
        updatedThresholds[index][field] = value;
        setSpendConfig({ ...spendConfig, thresholds: updatedThresholds });
    };

    const handleRemoveThreshold = (index) => {
        const updatedThresholds = spendConfig.thresholds.filter((_, i) => i !== index);
        setSpendConfig({ ...spendConfig, thresholds: updatedThresholds });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const newProgram = {
            name: programName,
            description: programDescription,
            type: programType,
            // Add specific config based on type
            ...(programType === 'Point-based' && { ...pointConfig }),
            ...(programType === 'Tiered' && { ...tieredConfig }),
            ...(programType === 'Spend-based' && { ...spendConfig }),
        };

        if (programToEdit) {
            onEdit({ ...programToEdit, ...newProgram });
        } else {
            onCreate(newProgram);
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                {/* Modal Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-800">{programToEdit ? 'Edit Loyalty Program' : 'Create New Loyalty Program'}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                {/* Modal Body */}
                <form onSubmit={handleSubmit} className="p-6">
                    <div className="mb-4">
                        <label htmlFor="programName" className="block text-gray-700 text-sm font-bold mb-2">
                            Program Name
                        </label>
                        <input
                            type="text"
                            id="programName"
                            value={programName}
                            onChange={(e) => setProgramName(e.target.value)}
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-indigo-500"
                            placeholder="e.g., Loyal Customer Club"
                            required
                        />
                    </div>

                    <div className="mb-6">
                        <label htmlFor="programDescription" className="block text-gray-700 text-sm font-bold mb-2">
                            Program Description
                        </label>
                        <textarea
                            id="programDescription"
                            value={programDescription}
                            onChange={(e) => setProgramDescription(e.target.value)}
                            rows="3"
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-indigo-500"
                            placeholder="Briefly describe the program's benefits."
                            required
                        ></textarea>
                    </div>

                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">Program Type</h3>
                        <div className="flex space-x-6">
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    className="form-radio h-5 w-5 text-indigo-600"
                                    name="programType"
                                    value="Point-based"
                                    checked={programType === 'Point-based'}
                                    onChange={() => setProgramType('Point-based')}
                                />
                                <span className="ml-2 text-gray-700">Point-based</span>
                            </label>
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    className="form-radio h-5 w-5 text-indigo-600"
                                    name="programType"
                                    value="Tiered"
                                    checked={programType === 'Tiered'}
                                    onChange={() => setProgramType('Tiered')}
                                />
                                <span className="ml-2 text-gray-700">Tiered Rewards</span>
                            </label>
                            <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    className="form-radio h-5 w-5 text-indigo-600"
                                    name="programType"
                                    value="Spend-based"
                                    checked={programType === 'Spend-based'}
                                    onChange={() => setProgramType('Spend-based')}
                                />
                                <span className="ml-2 text-gray-700">Spend-based Discounts</span>
                            </label>
                        </div>
                    </div>

                    {/* Conditional Configuration Section */}
                    {programType === 'Point-based' && (
                        <div className="mb-6 border p-4 rounded-lg bg-gray-50">
                            <h4 className="font-semibold text-gray-700 mb-3">Point Program Settings</h4>
                            <div className="mb-4">
                                <label htmlFor="earnRate" className="block text-gray-700 text-sm font-bold mb-2">
                                    Earn Rate
                                </label>
                                <input
                                    type="text"
                                    id="earnRate"
                                    value={pointConfig.earnRate}
                                    onChange={(e) => setPointConfig({ ...pointConfig, earnRate: e.target.value })}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-indigo-500"
                                    placeholder="e.g., 1 point per $1 spent"
                                    required
                                />
                            </div>
                            <div>
                                <label htmlFor="redemptionRate" className="block text-gray-700 text-sm font-bold mb-2">
                                    Redemption Rate
                                </label>
                                <input
                                    type="text"
                                    id="redemptionRate"
                                    value={pointConfig.redemptionRate}
                                    onChange={(e) => setPointConfig({ ...pointConfig, redemptionRate: e.target.value })}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-indigo-500"
                                    placeholder="e.g., 100 points = $5 discount"
                                    required
                                />
                            </div>
                        </div>
                    )}

                    {programType === 'Tiered' && (
                        <div className="mb-6 border p-4 rounded-lg bg-gray-50">
                            <h4 className="font-semibold text-gray-700 mb-3">Tiered Rewards Settings</h4>
                            {tieredConfig.tiers.map((tier, index) => (
                                <div key={index} className="mb-4 p-3 border rounded bg-white relative">
                                    <h5 className="font-medium text-gray-700 mb-2">Tier {index + 1}</h5>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor={`tierName-${index}`} className="block text-gray-700 text-sm font-bold mb-1">
                                                Tier Name
                                            </label>
                                            <input
                                                type="text"
                                                id={`tierName-${index}`}
                                                value={tier.name}
                                                onChange={(e) => handleUpdateTier(index, 'name', e.target.value)}
                                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-indigo-500"
                                                placeholder="e.g., Gold, Silver, Bronze"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor={`tierCriteria-${index}`} className="block text-gray-700 text-sm font-bold mb-1">
                                                Criteria to Reach
                                            </label>
                                            <input
                                                type="text"
                                                id={`tierCriteria-${index}`}
                                                value={tier.criteria}
                                                onChange={(e) => handleUpdateTier(index, 'criteria', e.target.value)}
                                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-indigo-500"
                                                placeholder="e.g., Spend $500, 10 purchases"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-4">
                                        <label htmlFor={`tierBenefits-${index}`} className="block text-gray-700 text-sm font-bold mb-1">
                                            Benefits
                                        </label>
                                        <textarea
                                            id={`tierBenefits-${index}`}
                                            value={tier.benefits}
                                            onChange={(e) => handleUpdateTier(index, 'benefits', e.target.value)}
                                            rows="2"
                                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-indigo-500"
                                            placeholder="e.g., 10% off, free shipping, exclusive access"
                                            required
                                        ></textarea>
                                    </div>
                                    {tieredConfig.tiers.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveTier(index)}
                                            className="absolute top-2 right-2 text-red-500 hover:text-red-700 transition-colors"
                                            title="Remove Tier"
                                        >
                                            <XMarkIcon className="h-5 w-5" />
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={handleAddTier}
                                className="mt-4 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                            >
                                + Add Another Tier
                            </button>
                        </div>
                    )}

                    {programType === 'Spend-based' && (
                        <div className="mb-6 border p-4 rounded-lg bg-gray-50">
                            <h4 className="font-semibold text-gray-700 mb-3">Spend-based Discount Settings</h4>
                            {spendConfig.thresholds.map((threshold, index) => (
                                <div key={index} className="mb-4 p-3 border rounded bg-white relative">
                                    <h5 className="font-medium text-gray-700 mb-2">Discount Rule {index + 1}</h5>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor={`spendAmount-${index}`} className="block text-gray-700 text-sm font-bold mb-1">
                                                Spend Amount ($)
                                            </label>
                                            <input
                                                type="number"
                                                id={`spendAmount-${index}`}
                                                value={threshold.amount}
                                                onChange={(e) => handleUpdateThreshold(index, 'amount', e.target.value)}
                                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-indigo-500"
                                                placeholder="e.g., 100"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor={`spendDiscount-${index}`} className="block text-gray-700 text-sm font-bold mb-1">
                                                Discount (e.g., $10 off or 10%)
                                            </label>
                                            <input
                                                type="text"
                                                id={`spendDiscount-${index}`}
                                                value={threshold.discount}
                                                onChange={(e) => handleUpdateThreshold(index, 'discount', e.target.value)}
                                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:border-indigo-500"
                                                placeholder="e.g., $10 off, 10% off"
                                                required
                                            />
                                        </div>
                                    </div>
                                    {spendConfig.thresholds.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveThreshold(index)}
                                            className="absolute top-2 right-2 text-red-500 hover:text-red-700 transition-colors"
                                            title="Remove Rule"
                                        >
                                            <XMarkIcon className="h-5 w-5" />
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={handleAddThreshold}
                                className="mt-4 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                            >
                                + Add Another Rule
                            </button>
                        </div>
                    )}

                    {/* Modal Footer */}
                    <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors shadow-md"
                        >
                            {programToEdit ? 'Save Changes' : 'Create Program'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateProgramModal;